import { Box, Typography } from '@mui/material'
import { Link, useLocation } from 'react-router-dom'
import { usePoolName } from '~hooks/use-pool-name'
import { COLORS } from '~theme/colors'
import { ReportType } from '../hooks/queries/reports/use-power-bi-reports-by-type'
import { useConstructLinkWithPool } from '../hooks/use-construct-link-with-pool'
import { ReportTileProps } from '~pages/pages-behind-login/reports/content'

export interface LinkTileProps extends Omit<ReportTileProps, 'reportType'> {
  reportType: ReportType
}

interface LinkTileRendererProps extends Omit<LinkTileProps, 'reportType'> {
  linkToPage: string
}

export function sanitizeReportType(reportType: LinkTileProps['reportType']) {
  return reportType.replace(/\s/g, '-').toLowerCase()
}

export function LinkTile(props: LinkTileProps) {
  const { pathname } = useLocation()
  const sanitizedReportType = sanitizeReportType(props.reportType)
  const rawLink = `${pathname}/${sanitizedReportType}`

  const linkToPage = useConstructLinkWithPool(rawLink)

  return <LinkTileRenderer {...props} linkToPage={linkToPage} />
}

function LinkTileRenderer({
  icon,
  title,
  description,
  linkToPage,
}: LinkTileRendererProps) {
  const poolName = usePoolName()

  return (
    <Box
      data-testid="link-tile"
      component={Link}
      to={linkToPage}
      sx={{
        color: COLORS.secondary.darkBlue,
        width: '100%',
        textDecoration: 'none',
        backgroundColor: COLORS.greys.ultraLight,
        paddingX: 4,
        paddingY: 8,
        display: 'flex',
        gap: 3,
        borderRadius: '4px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: COLORS.primary.white,
          boxShadow: '0px 4px 25px 0px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      {icon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
          width="28px"
          height="32px"
        >
          {icon}
        </Box>
      )}
      <Box>
        <Typography variant="h2">{`${title} ${poolName}`}</Typography>
        <Typography variant="paragraph1Secondary">{description}</Typography>
      </Box>
    </Box>
  )
}
